// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useRef, useEffect } from 'react';
import type { TrackReferenceOrPlaceholder, WidgetState } from '@livekit/components-core';
import { isEqualTrackRef, isTrackReference, isWeb } from '@livekit/components-core';
import { MessageFormatter, useRoomContext } from '@livekit/components-react';
import {
  useTracks,
  useCreateLayoutContext,
  usePinnedTracks,
  LayoutContextProvider,
  GridLayout,
  // FocusLayout,
  // FocusLayoutContainer,
  CarouselLayout,
  Chat,
  useConnectionState,
  useRemoteParticipants,
} from '@livekit/components-react';
import {
  ConnectionState,
  DisconnectReason,
  RemoteParticipant,
  RemoteTrackPublication,
  RoomEvent,
  Track,
  TrackPublication,
} from 'livekit-client';
import { ParticipantTile } from './participant-tile';
import { Loader2Icon } from 'lucide-react';
import { FocusLayout, FocusLayoutContainer } from './layouts/focus-layout';
import '@livekit/components-styles';
import '@/styles/call.css';
import { toast } from 'sonner';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { useGlobalSFX } from '@/context/sfx-context/use-sfx.ts';
import { ChannelType, UserPlan } from '@/models';
import { useLowCPUOptimizer } from '@/lib/livekit/usePerfomanceOptimiser.ts';
import { useCaptions } from '@/context/caption-context/use-captions.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { CaptionOverlay } from '@/context/caption-context/caption-overlay.tsx';
import { useSettings } from '@/context/settings-context/settings-context';
import { API_BASE } from '@/config';
import { RemotePlayOverlay } from './remote-play/remote-play-overlay';
import { useBreakpoint } from '@/hooks/use-breakpoint';

/**
 * @public
 */
export interface VideoConferenceProps extends React.HTMLAttributes<HTMLDivElement> {
  chatMessageFormatter?: MessageFormatter;
  chatId: string;
  /**
   * @deprecated, use ControlBar separately
   */
  onEndCall?: () => void;
  /** @alpha */
  SettingsComponent?: React.ComponentType;
  onDisconnected: () => void;
  channelType: ChannelType;
  plan?: UserPlan | null;
  serverId?: string;
}

/**
 * The `VideoConference` ready-made component is your drop-in solution for a classic video conferencing application.
 * It provides functionality such as focusing on one participant, grid view with pagination to handle large numbers
 * of participants, basic non-persistent chat, screen sharing, and more.
 *
 * @remarks
 * The component is implemented with other LiveKit components like `FocusContextProvider`,
 * `GridLayout`, `ControlBar`, `FocusLayoutContainer` and `FocusLayout`.
 * You can use this components as a starting point for your own custom video conferencing application.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <VideoConference />
 * <LiveKitRoom>
 * ```
 * @public
 */
export function VideoConference({
  chatMessageFormatter,
  SettingsComponent,
  chatId,
  onEndCall,
  onDisconnected,
  channelType,
  plan,
  serverId,
  ...props
}: VideoConferenceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [widgetState, setWidgetState] = React.useState<WidgetState>({
    showChat: true,
    unreadMessages: 0,
    showSettings: false,
  });
  const { receiveScreenShare } = useSettings();
  const lastAutoFocusedScreenShareTrack = React.useRef<TrackReferenceOrPlaceholder | null>(null);
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false }
  );
  const room = useRoomContext();
  const lowPowerMode = useLowCPUOptimizer(room);
  const { addCaption } = useCaptions();
  const socket = useSocket();
  const { isMd: isDesktop } = useBreakpoint('md');
  // Channel activity tracking - join/leave logging
  const hasLoggedJoinRef = useRef(false);

  useEffect(() => {
    if (!room || !serverId) return;

    const handleConnected = () => {
      if (!hasLoggedJoinRef.current) {
        hasLoggedJoinRef.current = true;
        fetch(`${API_BASE}/api/channel-activity/system`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channelId: chatId,
            serverId,
            action: 'join',
          }),
        }).catch((err) => console.error('Failed to log channel join:', err));
      }
    };

    /**
     * @experimental Only triggers when connection issues (NOT when user push Leave)
     * @param reason
     */
    const handleDisconnected = (reason: DisconnectReason | undefined) => {
      if (hasLoggedJoinRef.current) {
        hasLoggedJoinRef.current = false;
        fetch(`${API_BASE}/api/channel-activity/system`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channelId: chatId,
            serverId,
            action: 'leave',
          }),
        }).catch((err) => console.error('Failed to log channel leave:', err));
      }
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);

    // Handle immediate reconnect (already connected on mount)
    if (room.state === 'connected') {
      handleConnected();
    }

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
    };
  }, [room, chatId, serverId]);

  React.useEffect(() => {
    if (lowPowerMode) {
      console.warn('Режим низкого потребления включен');
    }
  }, [lowPowerMode]);

  const widgetUpdate = (state: WidgetState) => {
    setWidgetState(state);
  };

  const layoutContext = useCreateLayoutContext();

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare);

  // TODO: if server ADMIN streams -> constant focusTrack and golden border.
  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = tracks.filter((track) => !isEqualTrackRef(track, focusTrack));

  const sfx = useGlobalSFX();

  React.useEffect(() => {
    // TODO: if server ADMIN streams -> constant focusTrack and golden border.

    // If screen share tracks are published, and no pin is set explicitly, auto set the screen share.
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      layoutContext.pin.dispatch?.({
        msg: 'set_pin',
        trackReference: screenShareTracks[0],
      });
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid
      )
    ) {
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
      lastAutoFocusedScreenShareTrack.current = null;

      setTimeout(() => {
        window.dispatchEvent(new Event('resize')); // force resize
      }, 50);
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source
      );
      if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
        layoutContext.pin.dispatch?.({
          msg: 'set_pin',
          trackReference: updatedFocusTrack,
        });
      }
    }
  }, [
    screenShareTracks
      .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
      .join(),
    focusTrack?.publication?.trackSid,
    tracks,
  ]);

  const participantsConnected = useRemoteParticipants({
    updateOnlyOn: [RoomEvent.ParticipantConnected],
  });

  React.useEffect(() => {
    if (!room) return;

    const handleParticipantConnected = (participant: RemoteParticipant) => {
      // const identity = participant.identity;

      sfx.newMemberConnected.play().catch?.(() => {
        // ignore autoplay/browser restrictions
      });
    };

    // Ещё лучше — разделять по контексту:
    //
    // Для приватного звонка → звук на join + leave.
    // Для группового канала → только join.
    // Для large rooms → вообще отключить оба, оставить toast/UI indicators.
    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      // удаляем из set, чтобы при повторном входе звук снова сыграл
      // playedParticipantsRef.current.delete(participant.identity);
      // sfx.disconnectedMember.play().catch?.();
    };

    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);

    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    return () => {
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);

      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [room, sfx]);

  // TODO: play EVERY TIME when connecting new member
  // BUG: when android connected (or new member connected) not playing
  // not toggling between docked !docked, switching channel
  // React.useEffect(() => {
  //   sfx.newMemberConnected.play();
  // }, [participantsConnected]);

  const [notification, setNotification] = React.useState<React.ReactElement | undefined>(undefined);
  // @ts-ignore
  const state = useConnectionState(props.room);

  React.useEffect(() => {
    switch (state) {
      case ConnectionState.Reconnecting:
        toast.info('Переподключение');
        setNotification(
          <>
            <Loader2Icon className="lk-spinner" /> Reconnecting
          </>
        );
        break;
      case ConnectionState.Connecting:
        setNotification(
          <>
            <Loader2Icon className="lk-spinner" /> Connecting
          </>
        );
        break;
      case ConnectionState.Disconnected:
        setNotification(<>Disconnected</>);
        onDisconnected();
        break;
      default:
        setNotification(undefined);
        break;
    }
  }, [state]);

  async function checkAndRequestMedia() {
    try {
      const micStatus = await window.electronAPI.getMediaStatus('microphone');
      const camStatus = await window.electronAPI.getMediaStatus('camera');

      const [micGranted, camGranted] = await Promise.all([
        micStatus === 'granted' || (await window.electronAPI.requestMediaAccess('microphone')),
        camStatus === 'granted' || (await window.electronAPI.requestMediaAccess('camera')),
      ]);

      return !(!micGranted || !camGranted);
    } catch (error) {
      console.error('Media permission error:', error);
      return false;
    }
  }

  React.useEffect(() => {
    async function startMedia() {
      if (!isPardusBrowser()) return;
      const hasAccess = await checkAndRequestMedia();
      if (!hasAccess) return;

      try {
        await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false, // request only voice for now
        });

        if (navigator.mediaDevices?.getDisplayMedia) {
          await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
          });
        }
      } catch (err) {
        console.error('Media access failed:', err);
      }
    }

    startMedia();
  }, []);

  // Listen for remote STT broadcasts
  React.useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'speech-to-text' && data.meta) {
          const { channelId, participantIdentity, caption, timestamp } = data.meta;

          if (channelId === chatId && caption) {
            const isLocal = room.localParticipant?.identity === participantIdentity;
            if (!isLocal) {
              addCaption({
                speaker: participantIdentity,
                text: caption,
                ts: timestamp || Date.now(),
              });
            }
          }
        }
      } catch (err) {
        console.error('Error parsing socket message for captions', err);
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, chatId, addCaption, room.localParticipant?.identity]);

  React.useEffect(() => {
    if (!room) return;

    const enforceScreenShareSubscription = () => {
      room.remoteParticipants.forEach((participant) => {
        participant.getTrackPublications().forEach((pub) => {
          if (
            pub.source === Track.Source.ScreenShare ||
            pub.source === Track.Source.ScreenShareAudio
          ) {
            (pub as RemoteTrackPublication).setSubscribed(receiveScreenShare);
          }
        });
      });
    };

    enforceScreenShareSubscription();

    const handleTrackPublished = (publication: RemoteTrackPublication) => {
      // RoomEvent.TrackPublished срабатывает только для remote-треков,
      // но проверка !publication.isLocal добавляет дополнительный уровень безопасности
      if (
        !publication.isLocal &&
        (publication.source === Track.Source.ScreenShare ||
          publication.source === Track.Source.ScreenShareAudio)
      ) {
        publication.setSubscribed(receiveScreenShare);
      }
    };

    room.on(RoomEvent.TrackPublished, handleTrackPublished);

    return () => {
      room.off(RoomEvent.TrackPublished, handleTrackPublished);
    };
  }, [room, receiveScreenShare]);
  return (
    <div className="lk-video-conference" ref={containerRef} {...props}>
      {isWeb() && (
        <LayoutContextProvider value={layoutContext} onWidgetChange={widgetUpdate}>
          {!focusTrack ? (
            <div className="lk-grid-layout-wrapper bg-background">
              <GridLayout tracks={tracks}>
                <ParticipantTile
                  participantsCount={participantsConnected.length}
                  channelType={channelType}
                />
              </GridLayout>
            </div>
          ) : (
            <div className="lk-focus-layout-wrapper bg-background">
              <FocusLayoutContainer>
                <CarouselLayout tracks={carouselTracks} orientation={'horizontal'}>
                  <ParticipantTile
                    participantsCount={participantsConnected.length}
                    channelType={channelType}
                  />
                </CarouselLayout>
                {focusTrack && <FocusLayout trackRef={focusTrack} />}
              </FocusLayoutContainer>
            </div>
          )}
          {/*TODO: show not only in app but in desktop web browser*/}
          {isPardusBrowser() && <CaptionOverlay plan={plan} />}
          <Chat
            style={{ display: widgetState.showChat ? 'grid' : 'none' }}
            messageFormatter={chatMessageFormatter}
          />
          {SettingsComponent && (
            <div
              className="settings-menu-modal lk-settings-menu-modal"
              style={{ display: widgetState.showSettings ? 'block' : 'none' }}
            >
              <SettingsComponent />
            </div>
          )}
        </LayoutContextProvider>
      )}
      {/* Remote Play overlay - only available in PardusBrowser (desktop app) */}
      {/* {isPardusBrowser() && (   */}
      {isPardusBrowser() && isDesktop && (
        <div className="remote-play-container">
          <RemotePlayOverlay plan={plan} />
        </div>
      )}
    </div>
  );
}
